import {
  liveSopStepSpanId,
  type LiveSopReplay,
} from 'src/components/agent-trace/liveSopSpans';
import type { Span, SpanStatus } from 'src/components/agent-trace/types';
import type { AgentDefinitionResponse, AgentRunSummary } from '@erp/rpc';

// ---------------------------------------------------------------------------
// "What is this agent trying to do" — derived entirely from data the Agent
// Monitor page already holds (the definition, the latest run, and the span tree
// the trajectory rail renders). No backend call, no LLM: the summary can't
// disagree with the trace beside it, and it costs nothing to keep live.
//
// React-free on purpose so the rules are unit-testable without a renderer.
// ---------------------------------------------------------------------------

/** The step the agent is on right now. Null unless the run is actually running. */
export interface TrajectoryNow {
  label: string;
  /** Absolute epoch ms this step began, or null when the span carried no usable start. */
  startedAtMs: number | null;
}

/**
 * One entry in the run's plan — a replayed procedure step, or (failing that) a task-loop phase.
 *
 * `id` is the SPAN's id, not a synthetic one: the plan is derived from the same tree the rail
 * renders, so clicking an entry can reveal the exact row that produced it.
 */
export interface TrajectoryPlanItem {
  id: string;
  label: string;
  status: SpanStatus;
  /** How a replayed step ran: `deterministic` | `healed` | `agentic` | `failed`. Null for phases. */
  mode?: string | null;
  /** Whether the step's declared post-condition held. Null when there was no machine verdict. */
  verified?: boolean | null;
  /** Why a failed step couldn't be reproduced. */
  error?: string | null;
}

export interface TrajectoryPlan {
  items: TrajectoryPlanItem[];
  /**
   * An authored procedure's steps, rather than the loop's own phases. Only these are a real plan —
   * someone wrote them down before the run and the run either reproduced them or didn't.
   */
  isAuthored: boolean;
  done: number;
  failed: number;
  /** The entry in flight, for the one-line collapsed strip. */
  current: TrajectoryPlanItem | null;
}

export interface TrajectorySignals {
  /** Spans anywhere in the tree that failed. */
  failed: number;
  /** Wall time so far (live) or total (finished). Null when unknown. */
  elapsedMs: number | null;
  /** Input + output tokens across the whole run. */
  tokens: number;
}

/** The queue task the live run is in. `id` links to the task when the phase recorded one. */
export interface TrajectoryTask {
  id: string | null;
  title: string;
}

/**
 * The brief handed to the operator doing the live work — the instruction on the running path,
 * not an earlier operator call that already finished.
 */
export interface TrajectoryInstruction {
  text: string;
  /** Display name of the span that received it, e.g. "Remote Desktop Operator". */
  operatorName: string;
}

export interface TrajectorySummary {
  goal: string | null;
  now: TrajectoryNow | null;
  /** The task phase on the path to the deepest running span. Null once the run has stopped. */
  task: TrajectoryTask | null;
  /** The operator instruction on that same path. Null when this run isn't driving an operator. */
  instruction: TrajectoryInstruction | null;
  plan: TrajectoryPlan | null;
  signals: TrajectorySignals;
}

function walk(spans: Span[], visit: (span: Span) => void): void {
  for (const span of spans) {
    visit(span);
    if (span.children?.length) walk(span.children, visit);
  }
}

/**
 * The path from a root down to the most specific thing in flight: the LAST running span that has
 * no running span beneath it.
 *
 * Deepest-and-last, not first: a task-loop run has a running phase span wrapping a running child
 * step wrapping a running tool call, and only the innermost one names what the agent is actually
 * doing. Reporting the outer phase would leave the card stuck on "Task: …" for the whole run.
 * The path is kept because the task title and the operator instruction live on the ancestors of
 * that innermost span, not on it.
 */
function deepestRunningPath(spans: Span[]): Span[] | null {
  let found: Span[] | null = null;
  function visit(list: Span[], path: Span[]): void {
    for (const span of list) {
      const next = [...path, span];
      if (span.status === 'running') {
        const hasRunningChild = span.children?.some(
          (child) => child.status === 'running',
        );
        if (!hasRunningChild) found = next;
      }
      if (span.children?.length) visit(span.children, next);
    }
  }
  visit(spans, []);
  return found;
}

const TASK_NAME_PREFIX = 'Task: ';

function textField(
  input: Record<string, unknown> | undefined,
  key: string,
): string | null {
  const value = input?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** The queue task a phase span is working, from its recorded title or its display name. */
function taskOf(span: Span): TrajectoryTask | null {
  if (!span.isPhase) return null;
  const fromInput = textField(span.toolInput, 'taskTitle');
  const fromName = span.name.startsWith(TASK_NAME_PREFIX)
    ? span.name.slice(TASK_NAME_PREFIX.length).trim()
    : '';
  const title = fromInput ?? (fromName || null);
  if (!title) return null;
  return { id: textField(span.toolInput, 'taskId'), title };
}

/** The operator brief on a span, when that span is the one that received it. */
function instructionOf(span: Span): TrajectoryInstruction | null {
  if (span.brief?.kind !== 'instruction') return null;
  const text = span.brief.headline.trim();
  if (!text) return null;
  return { text, operatorName: span.name };
}

function firstDefined<T>(values: Array<T | null>): T | null {
  return values.find((value): value is T => value != null) ?? null;
}

/**
 * The task and the operator instruction that explain the deepest running span.
 *
 * Both are read off the path TO that span, nearest first. An earlier operator call that already
 * finished — and the instruction it was given — is not on this path, so a long run doesn't keep
 * showing the previous task's brief.
 */
function liveWork(path: Span[] | null): {
  task: TrajectoryTask | null;
  instruction: TrajectoryInstruction | null;
} {
  if (!path) return { task: null, instruction: null };
  const fromLeaf = [...path].reverse();
  return {
    task: firstDefined(fromLeaf.map(taskOf)),
    instruction: firstDefined(fromLeaf.map(instructionOf)),
  };
}

/**
 * The steps of every procedure this run replayed, in the order they ran.
 *
 * These are the only entries in the summary that are a plan in the real sense: a person authored
 * the procedure, so each step is an intention the run either met or missed — as opposed to a
 * phase, which is just the shape of the loop that happened to execute.
 */
function sopStepItems(spans: Span[]): TrajectoryPlanItem[] {
  const items: TrajectoryPlanItem[] = [];
  walk(spans, (span) => {
    if (span.sopStepIndex == null) return;
    items.push({
      id: span.id,
      label: span.name,
      status: span.status,
      mode: span.sopStepMode ?? null,
      verified: span.sopVerify ? span.sopVerify.passed : null,
      error: span.stopReason ?? null,
    });
  });
  return items;
}

/**
 * Procedure steps as the LIVE stream sees them, for the window where the run has moved on but the
 * persisted timeline (a 3s poll behind, and only written when a step's boundary row lands) hasn't
 * caught up.
 *
 * Each entry's id IS the span id its row will have (`liveSopStepSpanId`, the helper the tree uses),
 * under the operator call that owns the replay — so a step meets its persisted twin, and a task
 * loop's tasks, each replaying the same procedure, don't meet each other.
 */
function liveSopStepItems({
  replay,
  owner,
}: LiveSopReplay): TrajectoryPlanItem[] {
  const running = replay.running;
  const items: TrajectoryPlanItem[] = replay.steps.map((step) => ({
    id: liveSopStepSpanId(owner, step),
    label: step.name,
    status: (step.mode === 'failed' ? 'failed' : 'completed') as SpanStatus,
    mode: step.mode,
    verified: step.verify?.ran ? step.verify.passed : null,
    error: step.error,
  }));

  // The step in flight has no entry in `steps` until it finishes — that list is the record of what
  // has ALREADY run. Appending it is what makes the strip say "on step 4" rather than "3 done".
  // Matched by row id, as the tree does: every procedure restarts its step index, so a settled step
  // of an earlier procedure can share the running step's index without being it.
  const runningId = running ? liveSopStepSpanId(owner, running) : null;
  if (running && runningId && !items.some((item) => item.id === runningId)) {
    items.push({
      id: runningId,
      label: running.name,
      status: 'running' as SpanStatus,
      mode: null,
      verified: null,
      error: null,
    });
  }
  return items;
}

function phaseItems(spans: Span[]): TrajectoryPlanItem[] {
  return spans
    .filter((span) => span.isPhase)
    .map((span) => ({ id: span.id, label: span.name, status: span.status }));
}

/**
 * The run's plan — its replayed procedure steps, or its task-loop phases when it replayed none.
 *
 * Null for a plain agent, and null for a SINGLE entry: a one-item "plan" is a heading with one
 * thing under it, and the tree three lines below already shows that row. Inventing a plan out of an
 * agent's top-level tool calls would be worse still — a flat call log presented as an intended
 * sequence.
 *
 * The denominator is deliberately absent. Only steps that have already run are persisted, so a
 * live run cannot know how many are left, and "3 of 4" while a fifth is still coming would report
 * progress the data can't support.
 */
function derivePlan(
  spans: Span[],
  liveSop: LiveSopReplay | null,
): TrajectoryPlan | null {
  // Persisted entries in the order they ran, then the stream's — keyed by span id, so a step
  // present in both appears ONCE, in its persisted place. The stream wins on conflict because it is
  // ahead by definition: the persisted row for a step isn't written until its boundary lands. Steps
  // only the stream has seen yet are the newest, so they land last.
  const merged = new Map<string, TrajectoryPlanItem>();
  for (const item of sopStepItems(spans)) merged.set(item.id, item);
  for (const item of liveSop ? liveSopStepItems(liveSop) : []) {
    merged.set(item.id, item);
  }

  const sop = [...merged.values()];
  const items = sop.length > 0 ? sop : phaseItems(spans);
  if (items.length < 2) return null;
  return {
    items,
    isAuthored: sop.length > 0,
    done: items.filter((i) => i.status === 'completed').length,
    failed: items.filter((i) => i.status === 'failed').length,
    current: items.find((i) => i.status === 'running') ?? null,
  };
}

function countFailed(spans: Span[]): number {
  let failed = 0;
  walk(spans, (span) => {
    if (span.status === 'failed') failed += 1;
  });
  return failed;
}

/**
 * Total tokens for the run. Summed over ROOT spans only: `rolled` is already the subtree total
 * (aggregateTokens), so walking deeper would count every nested call twice.
 */
function countTokens(spans: Span[]): number {
  let tokens = 0;
  for (const span of spans) {
    tokens +=
      (span.rolled?.input ?? span.inputTokens ?? 0) +
      (span.rolled?.output ?? span.outputTokens ?? 0);
  }
  return tokens;
}

function elapsedMs(run: AgentRunSummary | null, nowMs?: number): number | null {
  if (!run) return null;
  // A finished run records its own duration; a live one hasn't yet, so measure against the clock
  // the caller passed. Without a clock a live run simply reports nothing rather than 0 — which
  // would read as "instant" instead of "unknown".
  // `>= 0`, not `> 0`: a run that finished in under a millisecond records 0, and treating that as
  // "absent" would fall through to the clock and report an elapsed that keeps growing after the
  // run has already ended.
  if (typeof run.duration === 'number' && run.duration >= 0)
    return run.duration;
  if (nowMs == null) return null;
  const started = Date.parse(run.startedAt);
  if (Number.isNaN(started)) return null;
  return Math.max(0, nowMs - started);
}

export function deriveTrajectorySummary({
  definition,
  run,
  spans,
  nowMs,
  liveSop = null,
}: {
  definition: Pick<AgentDefinitionResponse, 'description'>;
  run: AgentRunSummary | null;
  spans: Span[];
  /** Current time, for a live run's elapsed. Injected so this stays pure and testable. */
  nowMs?: number;
  /**
   * Procedure progress off the live AG-UI stream, resolved against `spans`
   * (`resolveLiveSopReplay`). Null for a finished run, or when nothing is streaming. The persisted
   * spans lag it by a poll plus however long the current step takes to finish, so during a live
   * replay this is the only source that knows where the run actually is.
   */
  liveSop?: LiveSopReplay | null;
}): TrajectorySummary {
  const goal = definition.description?.trim() || null;

  const isLive = run?.status === 'running';
  const runningPath = isLive ? deepestRunningPath(spans) : null;
  const runningSpan = runningPath
    ? (runningPath[runningPath.length - 1] ?? null)
    : null;
  const live = liveWork(runningPath);
  const runStart = run ? Date.parse(run.startedAt) : Number.NaN;
  // The persisted tree names the most SPECIFIC thing in flight (the individual click), so prefer
  // it. Early in a step nothing is persisted yet, and the stream's running step is all there is —
  // "Select TESTCO" beats an empty Now row.
  const nowLabel =
    runningSpan?.name ??
    (isLive ? (liveSop?.replay.running?.name ?? null) : null);
  const now: TrajectoryNow | null = nowLabel
    ? {
        label: nowLabel,
        // Only the persisted span knows when this started; a streamed step carries no offset, so
        // its Now row reports a label without a clock rather than a made-up one.
        startedAtMs:
          runningSpan && !Number.isNaN(runStart)
            ? runStart + runningSpan.startMs
            : null,
      }
    : null;

  return {
    goal,
    now,
    task: live.task,
    instruction: live.instruction,
    plan: derivePlan(spans, liveSop),
    signals: {
      failed: countFailed(spans),
      elapsedMs: elapsedMs(run, nowMs),
      tokens: countTokens(spans),
    },
  };
}
